import React, { useMemo } from 'react';
import { X, Zap, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import { selectRuleAttachPoint, detectSkipRuleCycles } from './skipLogic';
import { htmlToPlainText } from './utils';

const RuleEditorModal = ({
  showRuleEditorModal,
  editingRule,
  setEditingRule,
  setShowRuleEditorModal,
  ruleFormData,
  setRuleFormData,
  items,
  screens,
  currentScreenIndex,
  selectedElement,
  navigationRules,
  setNavigationRules,
  setConfirmDialog,
  saveNavigationRulesToBackend
}) => {
  // Build list of all question elements across all screens for "attach to" dropdown
  const allQuestionElements = useMemo(() => {
    const elements = [];
    screens.forEach((screen, screenIdx) => {
      if (!screen.elements) return;
      screen.elements.forEach(el => {
        if (el.type === 'question') {
          // Try multiple property paths for question text
          const rawText = el.content || el.questionText || el.data?.text || el.itemData?.text || el.text || el.label || '';
          const displayText = htmlToPlainText(rawText).substring(0, 50) || 'Question';
          elements.push({
            id: el.id,
            questionId: el.questionId,  // Database question ID for backend evaluation
            screenIndex: screenIdx,
            screenName: screen.name || `Screen ${screenIdx + 1}`,
            questionText: displayText,
            screen_question_id: el.screen_question_id
          });
        }
      });
    });
    return elements;
  }, [screens]);

  // Helper: Convert element IDs to question IDs for backend storage
  const convertElementIdsToQuestionIds = (elementIds) => {
    if (!elementIds || elementIds.length === 0) return [];
    return elementIds.map(elemId => {
      const el = allQuestionElements.find(q => q.id === elemId);
      // Return the database questionId if available, otherwise fall back to element ID
      return el?.questionId ? String(el.questionId) : elemId;
    });
  };

  // Determine the effective element ID for saving (either pre-selected or chosen in dropdown)
  const effectiveElementId = selectedElement || ruleFormData.attachToElement;
  
  if (!showRuleEditorModal) return null;

  const handleClose = () => {
    setShowRuleEditorModal(false);
    setEditingRule(null);
  };

  const handleSaveRule = async () => {
    // Validation
    if (!ruleFormData.condition_type || !ruleFormData.action_type) {
      toast.error('Please select both condition type and action type');
      return;
    }

    if (ruleFormData.condition_type === 'cumulative_score' && (!ruleFormData.question_ids || ruleFormData.question_ids.length === 0)) {
      toast.error('Please select at least one question for cumulative score condition');
      return;
    }

    if (ruleFormData.condition_type === 'answer_combination' && (!ruleFormData.conditions || ruleFormData.conditions.length === 0)) {
      toast.error('Please add at least one question condition for answer combination');
      return;
    }

    // Validate answer_combination conditions have values
    if (ruleFormData.condition_type === 'answer_combination') {
      const invalidCond = ruleFormData.conditions.find(c => c.value === '' || c.value === undefined);
      if (invalidCond) {
        toast.error('Please fill in all condition values');
        return;
      }
    }

    if (ruleFormData.action_type === 'skip_to' && !ruleFormData.target_screen_id) {
      toast.error('Please select a target screen for skip action');
      return;
    }

    // Auto-determine target element from question selection if not pre-selected
    // Use the first question in the condition
    const autoTargetElement = ruleFormData.condition_type === 'answer_combination'
      ? ruleFormData.conditions?.[0]?.question_id
      : ruleFormData.question_ids?.[0];
    if (!selectedElement && !autoTargetElement) {
      toast.error('Please select at least one question in the condition');
      return;
    }

    const targetElementId = selectRuleAttachPoint({
      conditionType: ruleFormData.condition_type,
      questionIds: ruleFormData.question_ids,
      conditions: ruleFormData.conditions,
      selectedElement,
      allQuestionElements,
    });

    // When editing, find the element the rule currently lives under. Cumulative
    // and combination rules may need to migrate if the attach-point logic now
    // resolves to a different element (e.g. legacy rules bound to selectedElement
    // instead of the last-screen question). Without this migration, updates to
    // such rules get silently dropped and the stale rule persists.
    const findCurrentAttachPoint = () => {
      if (!editingRule?.id) return null;
      for (const [elId, rules] of Object.entries(navigationRules || {})) {
        if ((rules || []).some(r => r.id === editingRule.id)) return elId;
      }
      return null;
    };
    const previousAttachPoint = findCurrentAttachPoint();

    // Helper function to actually save the rule
    const doSaveRule = async () => {
      // Find target screen and store both index and name for reliable matching
      // Use string coercion since dropdown values are strings but screen.id may be number
      const targetScreenIndex = screens.findIndex(s => String(s.id) === String(ruleFormData.target_screen_id));
      const targetScreen = screens[targetScreenIndex];

      // Convert element IDs to question IDs for backend evaluation
      // The backend needs actual question database IDs to match against participant responses
      const backendQuestionIds = convertElementIdsToQuestionIds(ruleFormData.question_ids || []);

      // For answer_combination, build backend conditions with proper question IDs
      let backendConditions = undefined;
      if (ruleFormData.condition_type === 'answer_combination' && ruleFormData.conditions) {
        backendConditions = ruleFormData.conditions.map(cond => {
          const el = allQuestionElements.find(q => q.id === cond.question_id);
          return {
            question_id: el?.questionId ? String(el.questionId) : cond.question_id,
            operator: cond.operator,
            value: cond.value
          };
        });
      }

      const ruleData = {
        ...ruleFormData,
        question_ids: ruleFormData.question_ids,
        backend_question_ids: backendQuestionIds,
        backend_conditions: backendConditions,
        target_screen_name: targetScreen?.name,
        target_screen_index: targetScreenIndex !== -1 ? targetScreenIndex : undefined
      };

      const ruleId = editingRule?.id || `rule_${Date.now()}`;
      const ruleWithId = { ...ruleData, id: ruleId };

      // Migrate between attach-points when editing moves the rule.
      const isMigration = editingRule && previousAttachPoint && previousAttachPoint !== targetElementId;

      const newTargetList = [...(navigationRules[targetElementId] || [])];
      if (editingRule) {
        const idx = newTargetList.findIndex(r => r.id === ruleId);
        if (idx !== -1) newTargetList[idx] = ruleWithId;
        else newTargetList.push(ruleWithId);
      } else {
        newTargetList.push(ruleWithId);
      }

      const previousList = isMigration
        ? (navigationRules[previousAttachPoint] || []).filter(r => r.id !== ruleId)
        : null;

      setNavigationRules(prev => {
        const next = { ...prev, [targetElementId]: newTargetList };
        if (isMigration) next[previousAttachPoint] = previousList;
        return next;
      });

      try {
        const targetInfo = allQuestionElements.find(el => el.id === targetElementId);
        const targetSqId = targetInfo?.screen_question_id;
        if (targetSqId) {
          await saveNavigationRulesToBackend(targetSqId, newTargetList);
        }
        if (isMigration) {
          const prevInfo = allQuestionElements.find(el => el.id === previousAttachPoint);
          const prevSqId = prevInfo?.screen_question_id;
          if (prevSqId) {
            await saveNavigationRulesToBackend(prevSqId, previousList);
          }
        }
        toast.success('Rule saved');
      } catch (error) {
        console.error('Error saving rule to backend:', error);
        toast.success('Rule created');
      }

      setShowRuleEditorModal(false);
      setEditingRule(null);
    };

    // Validation for duplicate/contradictory rules — read from the same attach
    // point the save will write to, so dupes on cumulative/combination rules
    // are caught even when targetElementId differs from selectedElement.
    const existingRules = navigationRules[targetElementId] || [];
    
    // Helper to compare question selections
    const sameQuestionSelection = (rule) => {
      const ruleQids = rule.question_ids || (rule.question_id ? [rule.question_id] : []);
      const formQids = ruleFormData.question_ids || (ruleFormData.question_id ? [ruleFormData.question_id] : []);
      // Both empty = current question (same)
      if (ruleQids.length === 0 && formQids.length === 0) return true;
      // Same length and same IDs
      if (ruleQids.length !== formQids.length) return false;
      return ruleQids.every(id => formQids.includes(id));
    };
    
    const duplicateRule = existingRules.find(rule => {
      if (editingRule && rule.id === editingRule.id) return false;
      return rule.condition_type === ruleFormData.condition_type &&
             rule.operator === ruleFormData.operator &&
             String(rule.value) === String(ruleFormData.value) &&
             sameQuestionSelection(rule);
    });

    if (duplicateRule) {
      if (duplicateRule.target_screen_id === ruleFormData.target_screen_id) {
        toast.error('A rule with the same condition and target already exists');
        return;
      } else {
        // Same condition but different target - show confirmation modal
        // Try multiple ways to resolve the screen name
        let existingScreenName = duplicateRule.target_screen_name;
        if (!existingScreenName) {
          // Try to find by frontend id
          let foundScreen = screens.find(s => String(s.id) === String(duplicateRule.target_screen_id));
          // Try to find by database survey_screen_id
          if (!foundScreen) {
            foundScreen = screens.find(s => String(s.survey_screen_id) === String(duplicateRule.target_screen_id));
          }
          // Try to find by target_screen_index
          if (!foundScreen && duplicateRule.target_screen_index !== undefined && duplicateRule.target_screen_index >= 0) {
            foundScreen = screens[duplicateRule.target_screen_index];
          }
          existingScreenName = foundScreen?.name || `Screen ${(duplicateRule.target_screen_index ?? -1) + 1 || duplicateRule.target_screen_id}`;
        }
        setConfirmDialog({
          isOpen: true,
          title: 'Contradictory Rule Warning',
          confirmText: 'Save Anyway',
          message: `A rule with the same condition already exists but skips to "${existingScreenName}". This will create contradictory behavior where only the first matching rule will execute. Do you want to continue anyway?`,
          type: 'warning',
          onConfirm: () => {
            setConfirmDialog(prev => ({ ...prev, isOpen: false }));
            doSaveRule();
          }
        });
        return;
      }
    }

    // Check for overlapping conditions (e.g., >= 5 and >= 3 both skip somewhere)
    // Only check overlap if rules are evaluating the SAME questions
    const overlappingRule = existingRules.find(rule => {
      if (editingRule && rule.id === editingRule.id) return false;
      if (rule.condition_type !== ruleFormData.condition_type) return false;
      if (rule.condition_type !== 'answer_value') return false;
      // Rules checking different questions can't overlap
      if (!sameQuestionSelection(rule)) return false;
      
      const ruleVal = parseFloat(rule.value);
      const newVal = parseFloat(ruleFormData.value);
      if (isNaN(ruleVal) || isNaN(newVal)) return false;
      
      return (
        (rule.operator === '>=' && ruleFormData.operator === '>=' && rule.target_screen_id !== ruleFormData.target_screen_id) ||
        (rule.operator === '<=' && ruleFormData.operator === '<=' && rule.target_screen_id !== ruleFormData.target_screen_id) ||
        (rule.operator === '==' && ruleFormData.operator === '==' && ruleVal === newVal && rule.target_screen_id !== ruleFormData.target_screen_id)
      );
    });

    if (overlappingRule) {
      setConfirmDialog({
        isOpen: true,
        title: 'Overlapping Rule Warning',
        confirmText: 'Save Anyway',
        message: 'This rule may overlap with an existing rule, potentially causing unpredictable navigation. Rules are evaluated in order - the first matching rule will be applied. Continue anyway?',
        type: 'warning',
        onConfirm: () => {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          doSaveRule();
        }
      });
      return;
    }

    // Cycle check: simulate the new/edited rule being in place and see if
    // that creates a static loop in the skip-rule graph. Static cycles are
    // almost always unintentional and create a trap the participant can't
    // escape without the runtime loop cap kicking in.
    if (ruleFormData.action_type === 'skip_to' && ruleFormData.target_screen_id) {
      const hypotheticalRule = {
        id: editingRule?.id || 'new_rule',
        action_type: 'skip_to',
        target_screen_id: ruleFormData.target_screen_id,
        target_screen_name: screens.find(s => String(s.id) === String(ruleFormData.target_screen_id))?.name,
        enabled: true,
      };
      const simulated = { ...navigationRules };
      const existingAtTarget = (simulated[targetElementId] || []).filter(r => r.id !== hypotheticalRule.id);
      simulated[targetElementId] = [...existingAtTarget, hypotheticalRule];
      // Also strip the rule from its previous attach-point if migrating
      if (previousAttachPoint && previousAttachPoint !== targetElementId) {
        simulated[previousAttachPoint] = (simulated[previousAttachPoint] || []).filter(r => r.id !== hypotheticalRule.id);
      }
      const cycle = detectSkipRuleCycles(simulated, allQuestionElements, screens);
      if (cycle) {
        setConfirmDialog({
          isOpen: true,
          title: 'Skip Logic Cycle Detected',
          confirmText: 'Save Anyway',
          message: `This rule creates a cycle in the skip logic: ${cycle.path.join(' → ')}. Participants may get stuck looping between these screens. A runtime safety cap will eventually break them out, but the flow will be confusing. Save anyway?`,
          type: 'warning',
          onConfirm: () => {
            setConfirmDialog(prev => ({ ...prev, isOpen: false }));
            doSaveRule();
          }
        });
        return;
      }
    }

    // No conflicts, save directly
    await doSaveRule();
  };

  // Build preview text
  const getPreviewText = () => {
    const conditionText = ruleFormData.condition_type ? (
      <>
        {ruleFormData.condition_type === 'answer_value' && 'Answer'}
        {ruleFormData.condition_type === 'cumulative_score' && (
          <>
            Total Score of{' '}
            <span className="font-semibold">
              {(ruleFormData.question_ids || []).length > 0 
                ? `${(ruleFormData.question_ids || []).length} question(s)`
                : 'selected questions'}
            </span>
          </>
        )}
        {ruleFormData.condition_type === 'answer_combination' && (
          <>
            <span className="font-semibold">
              {(ruleFormData.conditions || []).length > 0 
                ? `${(ruleFormData.conditions || []).length} condition(s)`
                : 'no conditions set'}
            </span>
            {' '}match ({ruleFormData.logic === 'or' ? 'ANY' : 'ALL'})
          </>
        )}
        {ruleFormData.condition_type !== 'answer_combination' && (
          <>{' '}{ruleFormData.operator} {ruleFormData.value || '___'}</>
        )}
      </>
    ) : '[condition not set]';

    const actionText = ruleFormData.action_type
      ? ruleFormData.action_type === 'skip_to'
        ? `Skip to ${screens.find(s => s.id === ruleFormData.target_screen_id)?.name || '[screen]'}`
        : ruleFormData.action_type === 'show_alert'
        ? 'Show Alert'
        : 'End Survey'
      : '[action not set]';

    return { conditionText, actionText };
  };

  const { conditionText, actionText } = getPreviewText();

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl dark:shadow-gray-900/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
        <div className="sticky top-0 bg-gradient-to-r from-primary-600 to-blue-600 px-6 py-5 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {editingRule ? 'Edit' : 'Add'} Skip Logic Rule
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-white/80 hover:text-white hover:bg-white/20 p-2 rounded-xl transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Condition Builder */}
          <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-4">
              <div className="p-1.5 bg-primary-100 text-primary-600 rounded-lg">
                <Settings className="w-4 h-4" />
              </div>
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Condition</h3>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Condition Type</label>
                <select
                  value={ruleFormData.condition_type}
                  onChange={(e) => setRuleFormData({ ...ruleFormData, condition_type: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                >
                  <option value="">-- Select Type --</option>
                  <option value="answer_value">Answer Value</option>
                  <option value="cumulative_score">Cumulative Score</option>
                  <option value="answer_combination">Answer Combination</option>
                </select>
              </div>
              
              {/* Logic selector for Answer Combination */}
              {ruleFormData.condition_type === 'answer_combination' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Logic</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setRuleFormData({ ...ruleFormData, logic: 'and' })}
                      className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-xl border-2 transition-all ${
                        ruleFormData.logic === 'and' || !ruleFormData.logic
                          ? 'bg-primary-50 border-primary-400 text-primary-700'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      AND - All must match
                    </button>
                    <button
                      type="button"
                      onClick={() => setRuleFormData({ ...ruleFormData, logic: 'or' })}
                      className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-xl border-2 transition-all ${
                        ruleFormData.logic === 'or'
                          ? 'bg-blue-50 border-blue-400 text-blue-700'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      OR - Any can match
                    </button>
                  </div>
                </div>
              )}

              {/* Standard operator/value for answer_value and cumulative_score */}
              {ruleFormData.condition_type !== 'answer_combination' && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <label className="block text-xs text-gray-600 mb-1">Operator</label>
                    <select
                      value={ruleFormData.operator}
                      onChange={(e) => setRuleFormData({ ...ruleFormData, operator: e.target.value })}
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value=">=">&gt;=</option>
                      <option value="<=">&lt;=</option>
                      <option value="==">==</option>
                      <option value="!=">!=</option>
                      <option value=">">&gt;</option>
                      <option value="<">&lt;</option>
                      <option value="in">in</option>
                      <option value="not_in">not in</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-600 mb-1">Value</label>
                    <input
                      type="text"
                      value={ruleFormData.value}
                      onChange={(e) => setRuleFormData({ ...ruleFormData, value: e.target.value })}
                      placeholder="Enter value..."
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  {ruleFormData.condition_type === 'answer_value' ? 'Question to evaluate' : 'Questions to evaluate'}
                  <span className="ml-2 text-[10px] text-primary-600 font-medium">
                    {ruleFormData.condition_type === 'cumulative_score' ? '(Sum all scores)' : 
                     ruleFormData.condition_type === 'answer_combination' ? '(Each with its own condition)' : 
                     '(Single question)'}
                  </span>
                </label>
                <p className="text-[10px] text-gray-500 mb-2">
                  {ruleFormData.condition_type === 'answer_value' 
                    ? 'Evaluate one question\'s answer against the threshold'
                    : ruleFormData.condition_type === 'cumulative_score'
                    ? 'Add up values from selected questions, compare total to threshold'
                    : 'Define conditions for multiple questions, combined with AND/OR logic'}
                </p>
                
                {/* Answer Value: Single select dropdown */}
                {ruleFormData.condition_type === 'answer_value' && (
                  <select 
                    value={ruleFormData.question_ids?.[0] || ''}
                    onChange={(e) => setRuleFormData({ 
                      ...ruleFormData, 
                      question_ids: e.target.value ? [e.target.value] : [] 
                    })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">-- Select a Question --</option>
                    {allQuestionElements.map((el) => (
                      <option key={el.id} value={el.id}>
                        [{el.screenName}] {el.questionText}
                      </option>
                    ))}
                  </select>
                )}
                
                {/* Cumulative Score: Multi-select checkboxes */}
                {ruleFormData.condition_type === 'cumulative_score' && (
                  <div className="border rounded-lg p-3 max-h-48 overflow-y-auto bg-gray-50 dark:bg-gray-900">
                    {allQuestionElements.length === 0 ? (
                      <p className="text-xs text-gray-500 italic">No questions available in this assessment</p>
                    ) : (
                      <div className="space-y-2">
                        {allQuestionElements.map((el) => (
                          <label key={el.id} className="flex items-start cursor-pointer hover:bg-white dark:hover:bg-gray-800 p-2 rounded transition-colors">
                            <input
                              type="checkbox"
                              checked={(ruleFormData.question_ids || []).includes(el.id)}
                              onChange={(e) => {
                                const currentIds = ruleFormData.question_ids || [];
                                const newIds = e.target.checked
                                  ? [...currentIds, el.id]
                                  : currentIds.filter(id => id !== el.id);
                                setRuleFormData({ ...ruleFormData, question_ids: newIds });
                              }}
                              className="w-4 h-4 mt-0.5 mr-3 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700 flex-1">
                              [{el.screenName}] {el.questionText}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                    {(ruleFormData.question_ids || []).length > 0 && (
                      <div className="mt-2 pt-2 border-t text-[10px] text-gray-600">
                        <strong>{(ruleFormData.question_ids || []).length}</strong> question(s) selected
                      </div>
                    )}
                  </div>
                )}

                {/* Answer Combination: Per-question conditions */}
                {ruleFormData.condition_type === 'answer_combination' && (
                  <div className="space-y-3">
                    {/* Configured conditions */}
                    {(ruleFormData.conditions || []).map((cond, idx) => {
                      const el = allQuestionElements.find(q => q.id === cond.question_id);
                      return (
                        <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-700 truncate">
                              [{el?.screenName || 'Screen'}] {el?.questionText || 'Question'}
                            </p>
                          </div>
                          <select
                            value={cond.operator}
                            onChange={(e) => {
                              const newConditions = [...(ruleFormData.conditions || [])];
                              newConditions[idx] = { ...cond, operator: e.target.value };
                              setRuleFormData({ ...ruleFormData, conditions: newConditions });
                            }}
                            className="w-16 px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-primary-500"
                          >
                            <option value=">=">≥</option>
                            <option value="<=">≤</option>
                            <option value="==">=</option>
                            <option value="!=">≠</option>
                            <option value=">">{'>'}</option>
                            <option value="<">{'<'}</option>
                          </select>
                          <input
                            type="text"
                            value={cond.value}
                            onChange={(e) => {
                              const newConditions = [...(ruleFormData.conditions || [])];
                              newConditions[idx] = { ...cond, value: e.target.value };
                              setRuleFormData({ ...ruleFormData, conditions: newConditions });
                            }}
                            placeholder="Value"
                            className="w-16 px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-primary-500"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newConditions = (ruleFormData.conditions || []).filter((_, i) => i !== idx);
                              setRuleFormData({ ...ruleFormData, conditions: newConditions });
                            }}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}

                    {/* Add question dropdown */}
                    <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-3">
                      <select
                        value=""
                        onChange={(e) => {
                          if (!e.target.value) return;
                          const newConditions = [...(ruleFormData.conditions || []), {
                            question_id: e.target.value,
                            operator: '>=',
                            value: ''
                          }];
                          setRuleFormData({ ...ruleFormData, conditions: newConditions });
                        }}
                        className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">+ Add Question Condition</option>
                        {allQuestionElements
                          .filter(el => !(ruleFormData.conditions || []).find(c => c.question_id === el.id))
                          .map((el) => (
                            <option key={el.id} value={el.id}>
                              [{el.screenName}] {el.questionText}
                            </option>
                          ))}
                      </select>
                    </div>

                    {(ruleFormData.conditions || []).length > 0 && (
                      <div className="text-[10px] text-gray-500 italic">
                        Rule triggers when <strong>{ruleFormData.logic === 'or' ? 'ANY' : 'ALL'}</strong> of the above conditions are met
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Builder */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">Action</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Action Type</label>
                <select
                  value={ruleFormData.action_type}
                  onChange={(e) => setRuleFormData({ ...ruleFormData, action_type: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">-- Select Action --</option>
                  <option value="skip_to">Skip to Screen</option>
                  <option value="show_alert">Show Alert</option>
                  <option value="end_survey">End Survey</option>
                </select>
              </div>

              {ruleFormData.action_type === 'skip_to' && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Target Screen</label>
                  <select
                    value={ruleFormData.target_screen_id}
                    onChange={(e) => setRuleFormData({ ...ruleFormData, target_screen_id: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">-- Select Screen --</option>
                    {screens.map((screen, idx) => (
                      <option key={screen.id} value={screen.id}>
                        {screen.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {(ruleFormData.action_type === 'show_alert' || ruleFormData.action_type === 'skip_to') && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Alert Message {ruleFormData.action_type === 'skip_to' ? '(optional)' : ''}
                  </label>
                  <textarea
                    value={ruleFormData.alert_message}
                    onChange={(e) => setRuleFormData({ ...ruleFormData, alert_message: e.target.value })}
                    placeholder="Message to display when condition is met..."
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500"
                    rows="3"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-blue-800 mb-1">Preview</p>
            <p className="text-sm text-blue-700">
              <strong>If</strong> {conditionText} <strong>then</strong> {actionText}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end space-x-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveRule}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          >
            {editingRule ? 'Update' : 'Save'} Rule
          </button>
        </div>
      </div>
    </div>
  );
};

export default RuleEditorModal;
